import axios from "axios";
import { BaseService } from "../BaseService";
import {
    ContentDetails,
    SignedUrlResponse,
    ThumbnailSignedUrlsResponse,
} from "JS/Models";
import { appConstants } from "JS/Helpers/Constants";
import { config } from "JS/Config";

export class MediaService extends BaseService {
    async getMediaBlob(url: string) {
        return axios({
            url: url,
            method: "GET",
            responseType: "blob",
        });
    }

    isBetaUser(): Promise<boolean> {
        return this.ngCall<boolean>(
            "get",
            appConstants.ngEndpoints.betaUser(config.user.memberId),
        ).then((response) => response.data);
    }

    getContentDetails(nid: string, type: string) {
        return this.ngCall<ContentDetails>(
            "get",
            appConstants.ngEndpoints.contentDetail(nid),
            {
                headers: {
                    authorization: `Bearer ${config.ngAccesstoken}`,
                },
                params: {
                    type,
                },
            },
        );
    }

    /**
     * @param bucket - S3 bucket name
     * @param postfix - Path/folder within bucket
     * @param filename - File name
     */
    getSignedUrl(
        bucket: string,
        postfix: string,
        filename: string,
    ): Promise<SignedUrlResponse> {
        return this.ngCall<SignedUrlResponse>(
            "get",
            appConstants.ngEndpoints.signedUrl,
            {
                params: {
                    bucket,
                    postfix,
                    filename,
                },
            },
        ).then((response) => response.data);
    }

    getSignedMediaUrl(
        bucket: string,
        postfix: string,
        filename: string,
    ): Promise<SignedUrlResponse> {
        return this.getSignedUrl(bucket, postfix, filename);
    }

    getSignedDocumentUrl(
        bucket: string,
        postfix: string,
        filename: string,
    ): Promise<SignedUrlResponse> {
        return this.getSignedUrl(bucket, postfix, filename);
    }

    getThumbnailSignedUrls(
        postfix: string,
        fileNames: string[],
    ): Promise<ThumbnailSignedUrlsResponse> {
        return this.ngCall<ThumbnailSignedUrlsResponse>(
            "post",
            appConstants.ngEndpoints.thumbnailSignedUrls,
            {
                postfix,
                fileNames,
            },
        ).then((response) => response.data);
    }

    async refreshSignedUrl(
        fullS3Url: string,
        bucket?: string,
    ): Promise<string> {
        if (!fullS3Url) return "";

        try {
            const url = new URL(fullS3Url);
            const pathParts = url.pathname.split("/").filter(Boolean);

            if (pathParts.length < 1) {
                throw new Error("Invalid S3 URL path");
            }

            const bucketName = bucket || url.hostname.split(".")[0];
            const filename = pathParts.pop();
            const postfix = "/" + pathParts.join("/");

            const response = await this.getSignedUrl(
                bucketName,
                postfix,
                filename,
            );
            return response.media_url;
        } catch (err) {
            console.error("Error refreshing signed URL:", err);
            throw err;
        }
    }

    async refreshSignedMediaUrl(fullS3Url: string): Promise<string> {
        return this.refreshSignedUrl(fullS3Url);
    }
}

export const mediaService = new MediaService();
